import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { subscribeLastIntrospectNodeByOperationAndParam } from '@adornis/introspect/api/get-introspect-node.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html, nothing, type TemplateResult } from 'lit';
import { archiveMails } from '../../api/archiveMails.js';
import { getEnrichedMailByID } from '../../api/getEnrichedMailByID.js';
import { unarchiveMails } from '../../api/unarchiveMails.js';
import { sendXmlByMail } from '../../features/export/api/sendXMLFromResult.js';
import { validateExtractionForDisplay, validateExtractionForSap } from '../../features/export/sap-validation.js';
import { ensureMailProcessing } from '../../features/information-extraction/api/ensureMailProcessing.js';
import { extractMailInformation } from '../../features/information-extraction/api/extractMailInformation.js';
import { subscribeRequestChanges } from '../../features/information-extraction/api/subscribeRequestChanges.js';
import { syncRequestQuestions } from '../../features/notifications/api/syncRequestQuestions.js';
import {
  MAIL_PROCESSING_EVALUATION_OPTIONS,
  MAIL_PROCESSING_NOT_EVALUATED_DOT,
  MAIL_PROCESSING_NOT_EVALUATED_LABEL,
  MailExtractionResult,
  MailProcessingEvaluation,
} from '../../features/information-extraction/db/MailExtractionResult.js';
import { ExtractionTimeline } from './ExtractionTimeline.js';
import { MailContentPanel } from './MailContentPanel.js';
import { MailExtractionResultView } from './MailExtractionResultView.js';
import { PipelineStatus } from './PipelineStatus.js';
import { RequestQuestions } from './RequestQuestions.js';
import { SecondaryTabs } from './SecondaryTabs.js';
import { useStickyQuery } from '../use-sticky-query.js';

// Rating affordance: a tiny coloured status dot is the only colour we add here — kept dezent
// per the monochrome system. The accent (#009ee1) stands in for "corrected".
interface RatingOption {
  value: MailProcessingEvaluation | null;
  label: string;
  description?: string;
  dot: string;
}
const RATING_NONE: RatingOption = { value: null, label: MAIL_PROCESSING_NOT_EVALUATED_LABEL, dot: MAIL_PROCESSING_NOT_EVALUATED_DOT };
const RATING_OPTIONS: RatingOption[] = [...MAIL_PROCESSING_EVALUATION_OPTIONS, RATING_NONE];

const closeMenus = () => (document.activeElement as HTMLElement | null)?.blur();

// A full extraction takes ~30-60s. If an introspect node has been "running" (no finishedAt)
// for far longer, the run was almost certainly killed mid-flight (e.g. a server restart) and
// will never finish — we surface a recovery state instead of an endless spinner.
const STALE_EXTRACTION_MS = 5 * 60 * 1000;

export const MailExtractionView = stateful(({ mailID }: { mailID: string }) => {
  const { value: introspectionNode, loading: isIntrospectionNodeLoading } = useQuery(
    () =>
      subscribeLastIntrospectNodeByOperationAndParam(
        'extractMailInformation',
        JSON.stringify({ mailID }),
      )({ _id: 1, startedAt: 1, finishedAt: 1 }),
    [mailID],
  );

  // Live marker of server-side changes to this request — answering a question, re-reading a
  // mail reply, a colleague's correction. Carries no content: it exists purely to give the
  // query below a dependency that moves when the stored extraction does.
  const { value: requestChanges } = useQuery(
    () => subscribeRequestChanges(mailID)({ _id: 1, validatedAt: 1, sentToSap: 1 }),
    [mailID],
  );
  const requestChangeToken = requestChanges
    ?.map(entry => `${entry.validatedAt?.getTime() ?? 0}:${entry.sentToSap?.length ?? 0}`)
    .join(',');

  // Sticky, because this query re-reads on every one of those signals — and each of them fires
  // while someone is working in the request. Dropping the extraction for the length of the
  // roundtrip would rebuild the whole right-hand pane from the "nothing extracted yet" card up.
  const { value: mailProcessing, refreshing: isRefreshingExtraction } = useStickyQuery(
    () =>
      ensureMailProcessing(mailID)({
        _id: 1,
        mailID: 1,
        evaluation: 1,
        notes: 1,
        sapNumber: 1,
        sentToSap: 1,
        extractionResult: MailExtractionResult.allFields,
      }),
    [mailID, JSON.stringify(introspectionNode?.toObject() ?? {}), requestChangeToken],
    [mailID],
  );

  // Bumped after (un)archiving so the header query re-reads `archivedAt` and the
  // action flips between "Archivieren" and "Aus Archiv holen".
  const [archivedTick, setArchivedTick] = useState(0);
  const { value: mailHead } = useStickyQuery(
    () => getEnrichedMailByID(mailID)({ _id: 1, subject: 1, receivedAt: 1, archivedAt: 1 }),
    [mailID, archivedTick],
    [mailID],
  );
  const isArchived = !!mailHead?.archivedAt;

  const mailExtractionResult = mailProcessing?.extractionResult;

  // Live, client-side mandatory-field validation. `validationReport` is the strict SAP gate
  // (drives the send button + pipeline "blocked" state); `displayReport` additionally surfaces
  // softer completeness issues for the error box only. The server re-validates on send.
  const validationReport =
    mailExtractionResult && !mailExtractionResult.notAnOrderDocument
      ? validateExtractionForSap(mailExtractionResult)
      : null;
  const displayReport =
    mailExtractionResult && !mailExtractionResult.notAnOrderDocument
      ? validateExtractionForDisplay(mailExtractionResult)
      : null;

  // Bridges the gap between user click and the subscription picking up the new introspect node,
  // so the button shows a loading state during the mutation → task → subscription roundtrip.
  // Scoped to mailID: the detail pane reuses this component instance across mails, so without
  // the [mailID] reset a re-extract on one mail would leak "extracting" onto every other mail.
  const [lastReextractAt, setLastReextractAt] = useState<number | null>(null, [mailID]);
  const [renderVersion, setRenderVersion] = useState(0);

  /**
   * Every path that edits the extraction ends here. Besides the re-render it tells the server
   * to bring this request's questions back in line: a position corrected by hand used to leave
   * its notification standing forever, because a question only ever closed when it was answered
   * through the question itself. It also re-persists `sapValidation`, which the manual
   * correction path never did — the status chip in the request list read a stale value from the
   * moment a position was corrected until the next full extraction.
   */
  const handleExtractionChanged = () => {
    setRenderVersion(renderVersion + 1);
    void syncRequestQuestions(mailID)().catch(() => undefined);
  };

  const isLocallyExtracting = !!(
    lastReextractAt &&
    (!introspectionNode?.finishedAt || new Date(introspectionNode.finishedAt).getTime() < lastReextractAt)
  );

  // A node that started long ago but never finished (and that we didn't just re-trigger
  // ourselves) is a stalled run — treat it as "interrupted", not "running".
  const nodeStartedAt = introspectionNode?.startedAt ? new Date(introspectionNode.startedAt).getTime() : null;
  const isStalled = !!(
    introspectionNode &&
    !introspectionNode.finishedAt &&
    !isLocallyExtracting &&
    nodeStartedAt != null &&
    Date.now() - nodeStartedAt > STALE_EXTRACTION_MS
  );

  // Only a first read counts as "extracting" — a re-read with the result still on screen is a
  // refresh, and treating it as a run swapped the whole pane for the spinner card every time a
  // question was answered or a colleague corrected something.
  const isExtracting = !!(
    isLocallyExtracting ||
    isIntrospectionNodeLoading ||
    (introspectionNode && !introspectionNode.finishedAt && !isStalled) ||
    !mailProcessing
  );

  // A genuinely running extraction (locally triggered or an unfinished introspect node) — as opposed
  // to `isExtracting`, which also flips true for plain data-loading. We key the step-by-step timeline
  // loader off this so it shows even on the very first extraction (when no result exists yet), instead
  // of falling through to the legacy button card.
  const isActivelyExtracting = !!(
    isLocallyExtracting || (introspectionNode && !introspectionNode.finishedAt && !isStalled)
  );

  // While a node is unfinished but not yet stale, re-render periodically so the staleness
  // check can flip to "interrupted" even when nothing pushes a new value (e.g. the run died
  // mid-flight and the subscription never updates again).
  const [, setStaleTick] = useState(0);
  useEffect(() => {
    if (!introspectionNode || introspectionNode.finishedAt || isLocallyExtracting || isStalled) return;
    const id = setInterval(() => setStaleTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, [introspectionNode?._id, introspectionNode?.finishedAt, isLocallyExtracting, isStalled]);

  const handleExtract = async () => {
    setLastReextractAt(Date.now());
    try {
      await extractMailInformation(mailID)();
    } catch (e) {
      setLastReextractAt(null);
      throw e;
    }
  };

  // Client-side download of the canonical document (step 1.5); the Markdown is already on the
  // entity, so no server endpoint is needed.
  const canonicalDocument = mailExtractionResult?.canonicalDocument ?? null;
  const downloadCanonicalDocument = () => {
    if (!canonicalDocument) return;
    const safeName = (mailExtractionResult?.getRequestID() ?? mailID).replace(/[^\w.-]+/g, '_');
    const blob = new Blob([canonicalDocument], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `anfrage-${safeName}.md`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleSetEvaluation = async (value: MailProcessingEvaluation | null) => {
    if (!mailProcessing) return;
    mailProcessing.evaluation = value;
    await mailProcessing.save();
    setRenderVersion(renderVersion + 1);
  };

  const handleArchive = () => {
    void XNativeDialog.waitFor(archiveMails([mailID])(), 'Anfrage wird archiviert…')
      .then(() => {
        goTo('/mails');
        return XSnackbar.show('Anfrage archiviert.');
      })
      .catch(err => XSnackbar.show('Fehler beim Archivieren: ' + err.message));
  };

  const handleUnarchive = () => {
    void XNativeDialog.waitFor(unarchiveMails([mailID])(), 'Anfrage wird wiederhergestellt…')
      .then(() => {
        setArchivedTick(archivedTick + 1);
        return XSnackbar.show('Anfrage aus dem Archiv geholt.');
      })
      .catch(err => XSnackbar.show('Fehler beim Wiederherstellen: ' + err.message));
  };

  const sendToSap = () => {
    if (!mailProcessing || !validationReport?.ok) return;
    XNativeDialog.waitFor(Promise.all([sendXmlByMail(mailProcessing.mailID, true)()]), 'Daten werden übermittelt...')
      .then(() => XSnackbar.show('Daten wurden übermittelt!'))
      .catch(err => XSnackbar.show('Fehler beim Übermitteln: ', err.message));
  };

  // ---- header pieces -------------------------------------------------------------------
  const sentCount = mailProcessing?.sentToSap?.length ?? 0;
  const requesting = mailExtractionResult?.requestingCompany;
  const headerSub = [requesting?.name, requesting?.partnerName || requesting?.contactPersonName]
    .filter(Boolean)
    .join(' · ');

  // Opening the extraction through a deep link (e.g. the validation reply mail) skips the list,
  // where the date is the only place it appears — so the header carries it too. Same format as
  // the list rows so both read alike.
  const receivedAt = mailHead?.receivedAt;
  const receivedLabel = receivedAt
    ? `${receivedAt.toLocaleDateString()}, ${receivedAt.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : '';

  const currentRating = RATING_OPTIONS.find(o => o.value === (mailProcessing?.evaluation ?? null)) ?? RATING_NONE;

  const renderRatingDropdown = () => html`
    <div class="dropdown dropdown-end">
      <div tabindex="0" role="button" class="btn btn-sm border border-base-300 bg-base-100 gap-2 font-semibold">
        <span class="h-1.75 w-1.75 rounded-full" style="background-color:${currentRating.dot}"></span>
        ${currentRating.label}
        <x-icon class="text-sm">expand_more</x-icon>
      </div>
      <ul tabindex="0" class="dropdown-content menu z-50 mt-2 w-80 rounded-xl border border-base-200 bg-base-100 p-1.5 shadow-lg">
        <li class="menu-title px-2 py-1 text-[10.5px] uppercase tracking-wider">Extraktion bewerten</li>
        ${RATING_OPTIONS.map(
          o => html`
            <li>
              <button
                class="items-start ${o.value === (mailProcessing?.evaluation ?? null) ? 'active font-semibold' : ''}"
                @click=${() => {
                  void handleSetEvaluation(o.value);
                  closeMenus();
                }}
              >
                <span class="h-1.75 w-1.75 mt-1.5 shrink-0 rounded-full" style="background-color:${o.dot}"></span>
                <!-- The ratings differ by *why* a result looks the way it does, not by how it looks —
                     without the hint the categories get picked interchangeably. -->
                <span class="flex flex-col gap-0.5 text-left">
                  <span>${o.label}</span>
                  ${o.description
                    ? html`<span class="text-[10.5px] font-normal leading-snug text-base-content/55">${o.description}</span>`
                    : nothing}
                </span>
              </button>
            </li>
          `,
        )}
      </ul>
    </div>
  `;

  const renderActionsDropdown = () => html`
    <div class="dropdown dropdown-end">
      <div tabindex="0" role="button" class="btn btn-sm btn-neutral gap-1.5">
        Aktionen
        <x-icon class="text-sm">expand_more</x-icon>
      </div>
      <ul tabindex="0" class="dropdown-content menu z-50 mt-2 w-60 rounded-xl border border-base-200 bg-base-100 p-1.5 shadow-lg">
        <li>
          <button
            ?disabled=${isExtracting}
            @click=${() => {
              void handleExtract();
              closeMenus();
            }}
          >
            <x-icon class="text-sm">refresh</x-icon> Erneut extrahieren
          </button>
        </li>
        <li>
          <button
            @click=${() => {
              if (mailProcessing) goTo(`/api/xml/${mailProcessing.mailID}`, { target: '_blank' });
              closeMenus();
            }}
          >
            <x-icon class="text-sm">download</x-icon> Download XML
          </button>
        </li>
        ${canonicalDocument
          ? html`<li>
              <button
                @click=${() => {
                  downloadCanonicalDocument();
                  closeMenus();
                }}
              >
                <x-icon class="text-sm">description</x-icon> Klartext
              </button>
            </li>`
          : nothing}
        ${sentCount
          ? html`
              <div class="my-1 h-px bg-base-200"></div>
              <li>
                <button
                  class="text-primary font-semibold"
                  ?disabled=${!validationReport?.ok}
                  @click=${() => {
                    sendToSap();
                    closeMenus();
                  }}
                >
                  <x-icon class="text-sm">send</x-icon> Erneut an SAP senden
                </button>
              </li>
            `
          : nothing}
        <div class="my-1 h-px bg-base-200"></div>
        <li>
          ${isArchived
            ? html`<button
                @click=${() => {
                  handleUnarchive();
                  closeMenus();
                }}
              >
                <x-icon class="text-sm">unarchive</x-icon> Aus Archiv holen
              </button>`
            : html`<button
                @click=${() => {
                  handleArchive();
                  closeMenus();
                }}
              >
                <x-icon class="text-sm">archive</x-icon> Archivieren
              </button>`}
        </li>
      </ul>
    </div>
  `;

  const renderActionCluster = () => html`
    <div class="flex items-center gap-2.5 flex-none">
      ${sentCount
        ? html`<div class="inline-flex items-center gap-1.5 rounded-full bg-base-200 px-3 py-1.5 text-xs font-semibold text-base-content/70" title=${mailProcessing?.sentToSap?.[sentCount - 1] ?? ''}>
            <x-icon class="text-sm">check</x-icon> An SAP gesendet · ${sentCount}×
          </div>`
        : html`<button
            class="btn btn-primary btn-sm gap-1"
            ?disabled=${!validationReport?.ok}
            title=${validationReport?.ok ? '' : 'Bitte zuerst die fehlenden Pflichtangaben ergänzen'}
            @click=${sendToSap}
          >
            <x-icon class="text-sm">send</x-icon> An SAP senden
          </button>`}
      ${renderRatingDropdown()} ${renderActionsDropdown()}
    </div>
  `;

  // ---- state-dependent right-panel bodies ---------------------------------------------
  const renderExtractionButton = () => html`
    <div class="rounded-2xl bg-base-100 border border-base-200 shadow-sm p-8 max-w-xl">
      <h2 class="text-lg font-bold">Mail-Inhalt extrahieren</h2>
      <p class="text-base-content/60 text-sm mt-1">
        Extrahiert den gesamten Inhalt der Mail inkl. PDF-Anhängen in strukturierte Daten.
      </p>
      <div class="mt-5">
        <button class="btn btn-primary" ?disabled=${isExtracting} @click=${handleExtract}>
          ${isExtracting
            ? html`<span class="loading loading-spinner loading-sm"></span> Extrahiere…`
            : html`<x-icon class="text-sm">upload_file</x-icon> Gesamten Inhalt extrahieren`}
        </button>
      </div>
    </div>
  `;

  const renderNotAnOrderDocument = () => html`
    <div class="rounded-2xl bg-base-100 border border-base-200 shadow-sm p-8 max-w-xl flex flex-col items-center text-center gap-3">
      <x-icon class="text-5xl text-warning">info</x-icon>
      <h2 class="text-xl font-bold">Keine Anfrage</h2>
      <p class="text-base-content/60">
        Das extrahierte Dokument wurde als <span class="font-semibold text-warning">keine Anfrage</span> erkannt. Bitte
        prüfen Sie die Mail manuell.
      </p>
      <button class="btn btn-primary mt-2" ?disabled=${isExtracting} @click=${handleExtract}>
        ${isExtracting
          ? html`<span class="loading loading-spinner loading-sm"></span> Extrahiere…`
          : html`<x-icon class="text-sm">refresh</x-icon> Erneut extrahieren`}
      </button>
    </div>
  `;

  const renderStalledCard = () => html`
    <div class="rounded-2xl bg-base-100 border border-base-200 shadow-sm p-8 max-w-xl flex flex-col items-center text-center gap-3">
      <x-icon class="text-5xl text-warning">sync_problem</x-icon>
      <h2 class="text-xl font-bold">Extraktion unterbrochen</h2>
      <p class="text-base-content/60">
        Die KI-Extraktion läuft seit über ${Math.round(STALE_EXTRACTION_MS / 60000)} Minuten ohne Ergebnis und wurde
        vermutlich abgebrochen (z. B. durch einen Server-Neustart). Du kannst sie erneut starten.
      </p>
      <button class="btn btn-primary mt-2" ?disabled=${isExtracting} @click=${handleExtract}>
        <x-icon class="text-sm">refresh</x-icon> Erneut extrahieren
      </button>
    </div>
  `;

  const renderExtractingCard = () => html`
    <div class="rounded-2xl bg-base-100 border border-base-200 shadow-sm p-8">
      <div class="flex flex-col items-center text-center mb-6">
        <span class="loading loading-spinner loading-lg text-primary mb-3"></span>
        <h3 class="text-lg font-semibold">KI-Extraktion läuft…</h3>
        <p class="text-base-content/60 text-sm max-w-md">Bearbeitung ist während des Vorgangs gesperrt.</p>
      </div>
      <div class="max-w-md w-full mx-auto">${ExtractionTimeline({ mailID, live: true })}</div>
      <!-- Escape hatch: if the run was killed mid-flight (e.g. server restart) the spinner would hang
           until the 5-min staleness timeout. This lets the user re-trigger immediately. -->
      <div class="flex flex-col items-center text-center mt-6 pt-5 border-t border-base-200">
        <p class="text-base-content/40 text-xs mb-2">Hängt fest? Die Extraktion wurde evtl. unterbrochen.</p>
        <button class="btn btn-ghost btn-sm text-base-content/60" @click=${handleExtract}>
          <x-icon class="text-sm">refresh</x-icon> Manuell neu starten
        </button>
      </div>
    </div>
  `;

  // The open questions of this request, answerable in place. Falls back to the plain list of
  // blocking fields when no notification exists — questions are only raised for a mail whose
  // forwarder is a registered internal user.
  const renderQuestions = () =>
    RequestQuestions({
      mailID,
      fallbackErrors: displayReport?.errors ?? [],
      onAnswered: () => setRenderVersion(renderVersion + 1),
    });

  // The state cards (nothing extracted yet / not an order / stalled run) own the whole
  // right-hand pane instead of sitting above content, so they are centred in it. Without the
  // wrapper their `max-w-xl` leaves them clinging to the top-left corner of a wide empty column.
  const centeredState = (card: TemplateResult) =>
    html`<div class="flex min-h-[40vh] items-center justify-center">${card}</div>`;

  const renderExtractionPanel = () => {
    if (isStalled) return centeredState(renderStalledCard());
    if (isActivelyExtracting) return renderExtractingCard();
    if (!mailExtractionResult) return centeredState(renderExtractionButton());
    if (isExtracting) return renderExtractingCard();
    if (mailExtractionResult.notAnOrderDocument) return centeredState(renderNotAnOrderDocument());
    return html`
      <div class="flex items-center gap-2 mb-4">
        <span class="text-[10.5px] font-semibold uppercase tracking-wider text-base-content/40">Extraktion</span>
        <span class="flex-1 h-px bg-base-300"></span>
      </div>
      ${renderQuestions()}
      <div class="space-y-4.5">
        ${MailExtractionResultView({ mailProcessing: mailProcessing!, onChange: handleExtractionChanged })}
        ${SecondaryTabs({
          mailID,
          mailProcessing: mailProcessing!,
          isExtracting,
          introspectionNodeID: introspectionNode?._id,
          canonicalDocument,
          onDownloadCanonical: downloadCanonicalDocument,
        })}
      </div>
    `;
  };

  const showActionCluster =
    !!mailExtractionResult && !mailExtractionResult.notAnOrderDocument && !isExtracting && !isStalled;

  return html`
    <div class="flex h-full min-h-0 flex-col bg-base-100">
      <!-- Fixed header: title + action cluster, then the quiet pipeline. -->
      <header class="flex-none border-b border-base-300 px-6 pt-4 bg-base-100">
        <div class="flex items-start justify-between gap-6">
          <div class="min-w-0">
            <div class="flex min-w-0 items-center gap-2">
              <div class="truncate text-lg font-bold tracking-tight text-base-content">${mailHead?.subject ?? ''}</div>
              <!-- The request re-reads itself whenever the server rewrites it — an answer mail
                   being evaluated, a colleague's correction. Saying so quietly is the whole point:
                   the alternative used to be the pane rebuilding itself with no explanation. -->
              ${isRefreshingExtraction
                ? html`<span
                    class="inline-flex shrink-0 items-center gap-1 text-xs text-base-content/45"
                    title="Die Anfrage wird neu eingelesen — es hat sich serverseitig etwas geändert."
                  >
                    <span class="loading loading-spinner loading-xs"></span>Aktualisiert…
                  </span>`
                : nothing}
            </div>
            ${receivedLabel || headerSub
              ? html`<div class="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-base-content/50">
                  ${receivedLabel
                    ? html`<span class="inline-flex shrink-0 items-center gap-1" title="Mail empfangen am ${receivedLabel}">
                        <x-icon class="text-[13px]">schedule</x-icon>${receivedLabel}
                      </span>`
                    : nothing}
                  ${receivedLabel && headerSub ? html`<span class="text-base-content/25">·</span>` : nothing}
                  ${headerSub ? html`<span class="truncate">${headerSub}</span>` : nothing}
                </div>`
              : nothing}
          </div>
          ${showActionCluster ? renderActionCluster() : nothing}
        </div>
        <div class="mt-4 pb-3.5">
          ${PipelineStatus(
            mailProcessing,
            isExtracting,
            !!(validationReport && !validationReport.ok && !sentCount && !isExtracting),
          )}
        </div>
      </header>

      <!-- 30/70 body: original mail on the left, extraction on the right, scrolling apart. -->
      <div class="flex-1 flex min-h-0">
        <div class="w-[31%] min-w-75 max-w-110 flex-none border-r border-base-300 h-full min-h-0">
          ${MailContentPanel({ mailID })}
        </div>
        <div class="flex-1 min-w-0 overflow-y-auto bg-base-200/50 px-6 py-6">${renderExtractionPanel()}</div>
      </div>
    </div>
  `;
});
